import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { useCelebration } from "../../contexts/CelebrationContext";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";

// DEFECT-D-13/D-14: the member-profile-side twin of AddMemberToClassDialog — pick an upcoming
// class at this member's branch and book them in, via the exact same `POST /bookings` staff
// booking endpoint (capacity/waitlist/entitlement rules all enforced identically).
export function BookMemberIntoClassDialog({ open, onOpenChange, memberId, memberName, branchId, testIds }) {
  const [scheduleId, setScheduleId] = useState("");
  const [error, setError] = useState("");
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();

  const schedulesQ = useQuery({
    queryKey: ["book-member-class-schedules", branchId],
    queryFn: async () => (await api.get("/class-schedules", { params: { branch_id: branchId } })).data.schedules,
    enabled: open && !!branchId,
  });
  const upcoming = (schedulesQ.data || []).filter((s) => new Date(s.start) > new Date());

  const bookMutation = useMutation({
    mutationFn: () => api.post("/bookings", { member_ref: memberId, schedule_id: scheduleId }),
    onSuccess: (res) => {
      const waitlisted = res.data?.status === "waitlisted";
      if (waitlisted) toast.success(`${memberName} added to the waitlist.`);
      else celebrate("class_booked", `${memberName} booked in.`);
      queryClient.invalidateQueries();
      setScheduleId("");
      onOpenChange(false);
    },
    onError: (e) => setError(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setScheduleId(""); setError(""); } }}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={testIds?.dialog}>
        <DialogHeader>
          <DialogTitle>Book {memberName} into a class</DialogTitle>
          <DialogDescription>Only upcoming classes at their branch are shown.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }}>{error}</p>}
        {upcoming.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No upcoming classes scheduled at this branch.</p>
        ) : (
          <Select onValueChange={setScheduleId} value={scheduleId || undefined}>
            <SelectTrigger data-testid={testIds?.select}><SelectValue placeholder="Pick a class" /></SelectTrigger>
            <SelectContent>
              {upcoming.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.class_type} · {new Date(s.start).toLocaleString()} · {s.confirmed_count}/{s.capacity}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <DialogFooter>
          <Button
            data-testid={testIds?.confirm} disabled={!scheduleId || bookMutation.isPending}
            onClick={() => { setError(""); bookMutation.mutate(); }} className="text-white" style={{ background: "var(--scarlet-accent)" }}
          >
            Book into class
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
